import ApexCharts, { ApexOptions } from "apexcharts";
import React from "react";
import ReactApexChart from "react-apexcharts";
import { twMerge } from "tailwind-merge";

import { SkeletonLoader } from "@fronterahealth/frontera-ui-components";

import { ApiSessionTargetProgressProgressTypeChoices } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

const progressTypeToEnum = Object.values(ApiSessionTargetProgressProgressTypeChoices).reduce<
  Record<string, ApiSessionTargetProgressProgressTypeChoices>
>((acc, choice) => {
  acc[convertReadableString(choice)] = choice;
  return acc;
}, {});

// Shared by the populated chart and the empty-state placeholder so a Duration/Frequency goal doesn't briefly render against a 0-100% axis on empty days.
// The generic "Actions" default below is deliberate and should stay: it is what keeps an unknown type from guessing at a percentage axis.
// It used to be reached far too often, because the type was inferred from the rows the date filter returned and an empty range left nothing to infer from.
// The backend now sends the target's own declared type even when the range is empty (INT-101), so this default is once again only for types we truly don't recognize.
export const yAxisForProgressType = (progressType?: string): ApexOptions["yaxis"] => {
  switch (progressTypeToEnum[progressType || ""]) {
    case ApiSessionTargetProgressProgressTypeChoices.Trial:
    case ApiSessionTargetProgressProgressTypeChoices.Interval:
    case ApiSessionTargetProgressProgressTypeChoices.TaskAnalysis:
    case ApiSessionTargetProgressProgressTypeChoices.Scale:
      return {
        min: 0,
        max: 100,
        title: { text: "% Correct" },
      };
    case ApiSessionTargetProgressProgressTypeChoices.Duration:
      return {
        min: 0,
        title: { text: "Time" },
        labels: {
          formatter: (value) => {
            const hours = Math.floor(value / 3600);
            const minutes = Math.floor((value % 3600) / 60);
            const seconds = value % 60;
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
          },
        },
      };
    case ApiSessionTargetProgressProgressTypeChoices.Frequency:
      return {
        min: 0,
        title: { text: "Occurrences" },
      };
    case ApiSessionTargetProgressProgressTypeChoices.Rate:
      return {
        min: 0,
        title: { text: "Occurrences per [hour/minute]" },
      };
    default:
      return {
        min: 0,
        title: { text: "Actions" },
      };
  }
};

/**
 * Existing wording for an empty chart. Kept as the default so this change does not
 * alter any copy on its own -- callers opt in to something more specific.
 */
export const DEFAULT_EMPTY_STATE_TEXT = "No data yet";

interface ChartDataPoint {
  x: string | number;
  y: number | null;
}

export interface ChartSeries {
  name: string;
  data: ChartDataPoint[];
}

export interface PhaseAnnotation {
  label: string;
  position: string | number;
  position2?: string | number; // For area annotations
  area: boolean;
  fillColor?: string;
}

export interface EventAnnotation {
  label: string;
  position: string | number;
}

interface SocialInteractionChartProps {
  series: ChartSeries[];
  phaseAnnotations: PhaseAnnotation[];
  eventAnnotations: EventAnnotation[];
  progressType?: string;
  masteryValue?: number; // in seconds (for duration chart)
  masteryLabel?: string;
  chartId: string;
  isLoading?: boolean;
  forClientProgramming?: boolean;
  chartRef?: React.RefObject<ApexCharts | null>;
  loaderClassName?: string;
  /** Override the x-axis min/max with the calendar's selected date range. */
  xAxisMin?: number;
  xAxisMax?: number;
  /**
   * Message shown when there is nothing to plot. Defaults to the existing wording.
   * Callers that know the chart is empty because of the selected date range rather
   * than because the target has never been run can pass their own text, so the two
   * cases stop looking identical to the clinician. The axis is unaffected either way.
   */
  emptyStateText?: string;
  /**
   * Lift the pen at each phase start instead of drawing one continuous line. Off by default:
   * only CentralReach graphs behave this way, so other EHRs keep their continuous line.
   */
  breakAtPhaseChange?: boolean;
}

export const SocialInteractionChart: React.FC<SocialInteractionChartProps> = ({
  series,
  phaseAnnotations,
  eventAnnotations,
  progressType,
  masteryValue,
  masteryLabel = "Mastery",
  chartId,
  isLoading = false,
  forClientProgramming,
  chartRef,
  loaderClassName = "",
  xAxisMin,
  xAxisMax,
  emptyStateText = DEFAULT_EMPTY_STATE_TEXT,
  breakAtPhaseChange = false,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center bg-white">
        <SkeletonLoader loading={isLoading}>
          <div
            className={twMerge("h-80", forClientProgramming ? "w-[55vw]" : "w-screen", "mx-16", loaderClassName)}
          ></div>
        </SkeletonLoader>
      </div>
    );
  }

  // Collect all timestamps from data points, events, and phase start/end dates
  const allXValues: number[] = [];

  // Add Series data points
  series.forEach((s) => {
    s.data.forEach((d) => {
      allXValues.push(new Date(d.x).getTime());
    });
  });

  // Add Event annotations
  eventAnnotations.forEach((a) => {
    allXValues.push(new Date(a.position).getTime());
  });

  // Add Phase annotations
  phaseAnnotations.forEach((a) => {
    allXValues.push(new Date(a.position).getTime());
    if (a.position2) {
      allXValues.push(new Date(a.position2).getTime());
    }
  });

  const xMin = xAxisMin ?? Math.min(...allXValues);
  const xMax = xAxisMax ?? Math.max(...allXValues);

  // Nothing to plot. Still resolve the y-axis from the declared progress type so an
  // empty range keeps the axis the goal actually uses instead of dropping to the generic one.
  if (!series || series.length === 0 || !series.some((s) => s.data.length > 0)) {
    const emptyChartOptions: ApexOptions = {
      chart: {
        id: `${chartId}-empty`,
        type: "line",
        height: forClientProgramming ? 300 : 400,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      xaxis: {
        type: "numeric",
        labels: {
          formatter: () => "",
        },
      },
      yaxis: yAxisForProgressType(progressType),
      grid: {
        borderColor: "#e5e7eb",
        strokeDashArray: 4,
      },
      noData: {
        text: emptyStateText,
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#9ca3af",
          fontSize: "16px",
        },
      },
    };

    return (
      <div className={`${!forClientProgramming ? "p-4" : ""} bg-white`}>
        <ReactApexChart
          options={emptyChartOptions}
          series={[]}
          type="line"
          height={forClientProgramming ? 300 : 400}
          chartRef={chartRef}
        />
      </div>
    );
  }

  // get the first series timestamp to filter out phase labels by
  let firstSeriesTimestamp: number;
  let firstSeriesPosition: string | number;
  series.forEach((item) => {
    const itemPosition = item?.data[0]?.x;
    const itemTimestamp = itemPosition ? new Date(itemPosition).getTime() : 0;
    if (itemPosition && !firstSeriesPosition) {
      firstSeriesPosition = itemPosition;
      firstSeriesTimestamp = itemTimestamp;
      return;
    }

    if (itemPosition && itemTimestamp < firstSeriesTimestamp) {
      firstSeriesPosition = itemPosition;
      firstSeriesTimestamp = itemTimestamp;
    }
  });

  const chartWidth = 530;
  const getPhaseMidpoint = (annPosition: string | number, index: number, allAnnotations: PhaseAnnotation[]) => {
    let targetPosition;

    const curPhasePosition = new Date(annPosition).getTime();
    const nextAnnotation = allAnnotations[index + 1];

    // if there is another annotation in the list, center between current and next
    if (nextAnnotation) {
      targetPosition = (curPhasePosition + new Date(nextAnnotation.position).getTime()) / 2;
    } else {
      targetPosition = (curPhasePosition + xMax) / 2;
    }

    // calculate offset from current annotation position to the next position/end of chart
    const timeDiff = targetPosition - curPhasePosition;
    const pixelOffset = (timeDiff / (xMax - xMin)) * chartWidth;

    return pixelOffset;
  };

  const yaxis: ApexOptions["yaxis"] = yAxisForProgressType(progressType);

  const chartOptions: ApexOptions = {
    chart: {
      id: chartId,
      type: "line",
      height: 400,
      toolbar: { show: false },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: false,
      },
    },
    colors: [
      "#00BFFF",
      "#FF69B4",
      "#8A2BE2",
      "#FFA500",
      "#228B22",
      "#DC143C",
      "#20B2AA",
      "#D2691E",
      "#FF4500",
      "#4169E1",
      "#6A5ACD",
      "#708090",
      "#FF6347",
      "#40E0D0",
      "#B22222",
    ],
    xaxis: {
      type: "numeric",
      min: xMin,
      max: xMax,
      tickAmount: 6,
      title: { text: "Date" },
      labels: {
        formatter: (value) => {
          // Numeric-axis ticks may be string millisecond timestamps (not parseable by Date(string)).
          const date = new Date(Number(value));
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        },
      },
    },
    yaxis,
    annotations: {
      xaxis: [
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...phaseAnnotations?.map((ann, index): any => {
          // Always show the vertical line at the correct position
          return {
            x: new Date(ann.position).getTime(),
            borderWidth: 2,
            strokeDashArray: 5,
            borderColor: "#775DD0",
            label: {
              style: {
                color: "#775DD0",
                background: "transparent",
                border: "none",
                borderWidth: 0,
                padding: 0,
                margin: 0,
                fontSize: "12px",
                fontWeight: "bold",
              },
              text: ann.label,
              orientation: "horizontal",
              offsetX: getPhaseMidpoint(ann.position, index, phaseAnnotations),
              offsetY: -10,
            },
          };
        }),

        ...eventAnnotations?.map((event) => ({
          x: new Date(event.position).getTime(),
          borderColor: "#6b7280",
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            text: event.label,
            offsetX: 20,
            style: {
              color: "#6b7280",
              background: "transparent",
              border: "none",
              borderWidth: 0,
              padding: 0,
              margin: 0,
              fontSize: "12px",
              fontWeight: "bold",
            },
          },
        })),
      ],
      yaxis:
        masteryValue !== undefined
          ? [
              {
                y: masteryValue,
                borderColor: "#FFA500",
                strokeDashArray: 5,
                label: {
                  text: masteryLabel,
                  style: { color: "#FFA500" },
                },
              },
            ]
          : [],
    },
    legend: { position: "bottom", show: true },
    stroke: { curve: "straight", width: 2 },
    markers: { size: 5 },
    dataLabels: { enabled: false },
    grid: {
      padding: {
        right: 75,
      },
    },
  };

  const toTimestamp = (x: string | number) => (typeof x === "number" ? x : new Date(x).getTime());

  // CentralReach lifts the pen at each phase change, so the line never joins the last point of one
  // phase to the first of the next. A null point on the boundary does the same here: ApexCharts
  // leaves a gap at a null. Boundaries outside the data (the first phase's start) add nothing.
  const phaseStarts = breakAtPhaseChange
    ? [...new Set((phaseAnnotations ?? []).map((a) => toTimestamp(a.position)))].sort((a, b) => a - b)
    : [];
  const breakAtPhaseStarts = (data: { x: number; y: number | null }[]) =>
    data.flatMap((point, i) => {
      const previous = data[i - 1];
      const breaks = previous
        ? phaseStarts.filter((start) => previous.x < start && start <= point.x).map((start) => ({ x: start, y: null }))
        : [];
      return [...breaks, point];
    });

  const filledSeries = series.map((s) => {
    // Ensure legends render even for empty series
    if (s.data.length === 0) {
      return { ...s, data: [{ x: xMin, y: null }] };
    }

    // ApexCharts has trouble consistently rendering a single data point series alongside other series. It causes graph lines to appear inconsistently especially when interacting with the legend.
    // To address this, we duplicate the single data point with a tiny time offset (1 millisecond).
    // This correctly renders the point without impacting the visual integrity of the chart.
    if (s.data.length === 1) {
      const singlePoint = s.data[0];
      const ts = toTimestamp(singlePoint.x);
      return {
        ...s,
        data: [
          { x: ts, y: singlePoint.y },
          { x: ts + 1, y: singlePoint.y },
        ],
      };
    }

    return { ...s, data: breakAtPhaseStarts(s.data.map((d) => ({ ...d, x: toTimestamp(d.x) }))) };
  });

  return (
    <div className={`${!forClientProgramming ? "p-4" : ""} bg-white`}>
      <ReactApexChart
        options={chartOptions}
        series={filledSeries}
        type="line"
        height={forClientProgramming ? 300 : 400}
        chartRef={chartRef}
      />
    </div>
  );
};
